import React, { useState } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useForm } from "react-hook-form";
import { Pencil, AlertCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { addDoctor } from "./service/doctor-service";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Controller } from "react-hook-form";

export default function AddDoctor() {
    const [profileImage, setProfileImage] = useState(null);
    const [imageError, setImageError] = useState("");       // This will now hold the preview (Base64)
    const [profileImageFile, setProfileImageFile] = useState(null);
    const { setLoading } = useLoader();
    const navigate = useNavigate()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
        control
    } = useForm();

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // File size check
        if (file.size > 5 * 1024 * 1024) {
            setImageError("File size must be less than 5MB");
            return;
        }

        // File type check
        if (!["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)) {
            setImageError("Only JPG, PNG, or GIF files are allowed");
            return;
        }

        setImageError("");

        // Store the actual File object
        setProfileImageFile(file);

        // Create preview (Base64) - only for UI
        const reader = new FileReader();
        reader.onloadend = () => {
            setProfileImage(reader.result); // Base64 string for preview
        };
        reader.readAsDataURL(file);
    };

    const onSubmit = async (data) => {
        setLoading(true)
        try {
            const form_data = new FormData();
            form_data.append("fname", data.firstName);
            form_data.append("lname", data.lastName);
            form_data.append("email", data.email);
            form_data.append("mobileNumber", data.mobile);
            form_data.append("degree", data.degree);
            form_data.append("hospitalName", data.hospital);
            form_data.append("commissionPercent", data.commision);
            form_data.append("address", data.address);
            form_data.append("profilePicture", profileImageFile);
            const res = await addDoctor(form_data);
            if (res.data.success == true) {
                toast.success(res.data.message);
                navigate(`${process.env.PUBLIC_URL}/doctors-list`)
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
        // console.log("Form submitted:", { ...data, profileImage });
        // alert("Doctor added successfully!");
        reset();
        setProfileImage(null);
    };

    return (
        <>
            <Breadcrumbs mainTitle="Add Doctor" parent="Home" subParent="Doctor List" title="Add Doctor" />

            <Container fluid={true}>
                <Row className="tw-flex tw-justify-center">
                    <Col md="12" lg="12">
                        <Card className="tw-shadow-xl tw-rounded-2xl tw-border-0 tw-bg-white tw-p-8">
                            <div className="row">
                                <div className="col-md-4">
                                    {/* Profile Image */}
                                    <div className="tw-flex tw-flex-col tw-items-center tw-mb-8">
                                        <div className="tw-relative">
                                            <label htmlFor="profile-upload" className="tw-cursor-pointer">
                                                <div className="tw-w-32 tw-h-32 tw-rounded-full tw-overflow-hidden tw-bg-gray-100 tw-border tw-border-gray-300 tw-flex tw-items-center tw-justify-center hover:tw-ring-2 hover:bg-primary tw-transition-all">
                                                    {profileImage ? (
                                                        <img
                                                            src={profileImage}
                                                            alt="Profile"
                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                        />
                                                    ) : (
                                                        <span className="tw-text-gray-400 tw-text-sm">Upload Image</span>
                                                    )}
                                                </div>
                                            </label>
                                            <label
                                                htmlFor="profile-upload"
                                                className="tw-absolute tw-bottom-2 tw-right-2 bg-primary tw-rounded-full tw-p-2 tw-shadow-md tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-border-2 tw-border-white"
                                            >
                                                <Pencil className="tw-w-4 tw-h-4 tw-text-white" />
                                            </label>
                                            <input
                                                id="profile-upload"
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png,image/gif"
                                                onChange={handleImageUpload}
                                                className="tw-hidden"
                                            />
                                        </div>
                                        {imageError && (
                                            <div className="tw-flex tw-items-center tw-gap-1 tw-text-red-600 tw-text-sm tw-mt-2">
                                                <AlertCircle className="tw-w-4 tw-h-4" />
                                                <span>{imageError}</span>
                                            </div>
                                        )}
                                        <p className="tw-text-gray-500 tw-text-xs tw-mt-1">
                                            {/* JPG, PNG or GIF. Max 5MB. (Optional) */}
                                            Profile Image
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-8">
                                    {/* Form */}
                                    <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-5">
                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                            {/* First Name */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    First Name <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter first name"
                                                    {...register("firstName", { required: "First name is required" })}
                                                />
                                                {errors.firstName && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.firstName.message}</p>
                                                )}
                                            </div>

                                            {/* Last Name */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Last Name <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter last name"
                                                    {...register("lastName", { required: "Last name is required" })}
                                                />
                                                {errors.lastName && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.lastName.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                            {/* Email */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Email <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="Enter email address"
                                                    {...register("email", {
                                                        required: "Email is required",
                                                        pattern: {
                                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                            message: "Enter a valid email address",
                                                        },
                                                    })}
                                                />
                                                {errors.email && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email.message}</p>
                                                )}
                                            </div>

                                            {/* Mobile Number*/}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Mobile Number <span className="tw-text-red-500">*</span>
                                                </label>
                                                 <Controller
                                                        name="mobile"
                                                        control={control}
                                                        rules={{
                                                            required: "Mobile is required",
                                                            minLength: {
                                                                value: 10,
                                                                message: "Invalid mobile number",
                                                            },
                                                        }}
                                                        render={({ field }) => (
                                                            <PhoneInput
                                                                {...field}
                                                                country="in"
                                                                enableSearch
                                                                placeholder="+91 9876543210"
                                                                inputClass="form-control"
                                                                inputStyle={{ width: "100%" }}
                                                                onChange={(value) => field.onChange(value)}
                                                            />
                                                        )}
                                                    />
                                                {/* <input
                                                    type="tel"
                                                    maxLength="10"
                                                    className="form-control"
                                                    placeholder="Enter mobile number"
                                                    {...register("mobile", {
                                                        required: "Mobile number is required",
                                                        pattern: {
                                                            value: /^[0-9]{10}$/,
                                                            message: "Enter a valid 10-digit number",
                                                        },
                                                    })}
                                                /> */}
                                                {errors.mobile && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.mobile.message}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                            {/* Degree (optional) */}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">Degree (Optional)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter degree"
                                                    {...register("degree")}
                                                />
                                            </div>

                                            {/* hospital*/}
                                            <div>
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Hospital <span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"

                                                    className="form-control"
                                                    placeholder="Enter hospital"
                                                    {...register("hospital", {
                                                        required: "Hospital is required",

                                                    })}
                                                />
                                                {errors.hospital && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.hospital.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="tw-block tw-font-medium tw-mb-1">
                                                    Commision (%)<span className="tw-text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Enter commision"
                                                    {...register("commision", {
                                                        required: "Commision is required",

                                                    })}
                                                />
                                                {errors.commision && (
                                                    <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.commision.message}</p>
                                                )}
                                            </div>
                                            <div className="col-md-8">
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">
                                                        Address <span className="tw-text-red-500">*</span>
                                                    </label>
                                                    <textarea
                                                        rows="1"
                                                        className="form-control"
                                                        placeholder="Enter full address"
                                                        {...register("address", { required: "Address is required" })}
                                                    />
                                                    {errors.address && (
                                                        <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.address.message}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>


                                        {/* Submit Button */}
                                        <div className="text-end tw-mt-6">
                                      
                                            <button
                                                type="submit"
                                                // disabled={isSubmitting}
                                                className="btn btn-primary me-3"
                                            >
                                                {isSubmitting ? "Submitting..." : "Add Doctor"}
                                            </button>
                                                  <Link to={`${process.env.PUBLIC_URL}/doctors-list`}>
                                                <button
                                                    className="btn btn-secondary"
                                                >
                                                    Cancel
                                                </button>
                                            </Link>
                                        </div>
                                    </form>
                                </div>
                            </div>

                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
